import ContractAttachment from '@/components/ContractAttachment'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import NegotiationDetailSkeleton from '@/components/skeletons/NegotiationDetail'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { ResolveNegotiationProps } from '@/modals/NegotiationModals/ResolveNegotiation'
import {
    NEGOTIATION_PRIORITY,
    NEGOTIATION_STAGES,
    NEGOTIATION_STATUS,
    NEGOTIATION_TYPES,
} from '@/modules/Customer/constants'
import {
    archiveNegotiation,
    cancelNegotiation,
    rejectNegotiation,
    resolveNegotiation,
} from '@/modules/Customer/services'
import useAuthStore from '@/stores/useAuthStore'
import {
    formatCurrency,
    formatDateWithFormatter,
    uniqBy,
} from '@/utils/function'
import { paths } from '@/utils/path'
import {
    CalendarDays,
    DollarSign,
    Hash,
    Layers,
    MessageSquare,
    StickyNote,
    User,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button, Chip } from 'vnetwork-ui'
import { Color } from 'vnetwork-ui/dist/types'
import { getContractNegotiationById } from '../../services'
import Archived from './Archived'
import History from './History'

// ============================================================================
// Helpers
// ============================================================================

/** Look up a label from a constant list */
const findLabel = (list: any[], value: string) =>
    list?.find((item) => item.value === value)

/** Compact info row */
const InfoItem = ({
    icon,
    label,
    value,
    className,
}: {
    icon?: React.ReactNode
    label: string
    value?: React.ReactNode
    className?: string
}) => (
    <div className={className}>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            {icon}
            {label}
        </div>
        {value ? (
            <div className="text-sm font-medium">{value}</div>
        ) : (
            <div className="text-sm text-gray-400 italic">---</div>
        )}
    </div>
)

// ============================================================================
// Component
// ============================================================================

const Detail: React.FC = () => {
    const { t } = useTranslation()
    const { id } = useParams<{ id: string }>()
    const { employee } = useAuthStore()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const [loadingActions, setLoadingActions] = useState<
        Record<string, boolean>
    >({})

    const { data: negotiationData, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.NEGOTIATIONS, id],
            queryFn: () => getContractNegotiationById(Number(id)),
        },
        {
            loadingComponent: (
                <>
                    <SubHeaders
                        breadcrumbs={[
                            {
                                title: t('negotiations.list'),
                                path: paths.contractNegotiation,
                            },
                            { title: '...', path: '#' },
                        ]}
                    />
                    <NegotiationDetailSkeleton />
                </>
            ),
        }
    )

    // Mutations
    const { mutateAsync: rejectNegotiationAsync } = useMutationWithHandler(
        { mutationFn: rejectNegotiation },
        {
            successMessage: t('negotiation-rejected-successfully'),
            reactQueryKeys: QUERY_KEYS.NEGOTIATIONS,
        }
    )
    const { mutateAsync: cancelNegotiationAsync } = useMutationWithHandler(
        { mutationFn: cancelNegotiation },
        {
            successMessage: t('negotiation-cancelled-successfully'),
            reactQueryKeys: QUERY_KEYS.NEGOTIATIONS,
        }
    )
    const { mutateAsync: archiveNegotiationAsync } = useMutationWithHandler(
        { mutationFn: archiveNegotiation },
        {
            successMessage: t('negotiation-archived-successfully'),
            reactQueryKeys: QUERY_KEYS.NEGOTIATIONS,
        }
    )

    // Permission checks
    const negotiation = negotiationData?.result
    const status = negotiation?.contract_negotiation_status || ''
    const isAssignee = negotiation?.contract_negotiation_assignees?.includes(
        employee?.employee_id as number
    )
    const canAct = isAssignee && !negotiation?.archived

    const canResolve =
        canAct &&
        ['draft', 'in_progress', 'pending_review', 'waiting_approval'].includes(
            status
        )
    const canReject =
        canAct &&
        ['draft', 'in_progress', 'pending_review', 'waiting_approval'].includes(
            status
        )
    const canCancel =
        canAct &&
        !['completed', 'cancelled', 'archived', 'converted'].includes(status)
    const canArchive = !negotiation?.archived && status === 'completed'

    // Action helpers with loading state
    const withLoading =
        (key: string, fn: (...args: any[]) => Promise<any>) =>
        async (...args: any[]) => {
            setLoadingActions((prev) => ({ ...prev, [key]: true }))
            try {
                await fn(...args)
            } finally {
                setLoadingActions((prev) => ({ ...prev, [key]: false }))
            }
        }

    const handleResolve = () => {
        setModal<ResolveNegotiationProps>(
            'NegotiationModals/ResolveNegotiation/index',
            {
                negotiationId: String(negotiation!.contract_negotiation_id),
                onSubmit: withLoading(`resolve_${id}`, (data: any) =>
                    resolveNegotiation({
                        id: String(negotiation!.contract_negotiation_id),
                        data,
                    })
                ),
            }
        )
    }

    const handleReject = () => {
        setModal('NegotiationModals/RejectNegotiation/index', {
            onSubmit: withLoading(`reject_${id}`, (data: any) =>
                rejectNegotiationAsync({
                    id: negotiation!.contract_negotiation_id,
                    data,
                })
            ),
        })
    }

    const handleCancel = () => {
        setModal('NegotiationModals/CancelNegotiation/index', {
            negotiationId: String(negotiation!.contract_negotiation_id),
            onSubmit: withLoading(`cancel_${id}`, (data: any) =>
                cancelNegotiationAsync({
                    id: String(negotiation!.contract_negotiation_id),
                    data,
                })
            ),
        })
    }

    const handleArchive = () => {
        setConfirmation({
            title: t('archive-negotiation'),
            description: t('archive-negotiation-description'),
            open: true,
            onConfirm: withLoading(`archive_${id}`, () =>
                archiveNegotiationAsync(negotiation!.contract_negotiation_id)
            ),
        })
    }

    // Render
    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    if (!negotiation) return <div>Negotiation not found</div>
    if (negotiation.archived) return <Archived negotiation={negotiation} />

    const statusObj = findLabel(NEGOTIATION_STATUS(t), status)
    const priorityObj = findLabel(
        NEGOTIATION_PRIORITY(t),
        negotiation.contract_negotiation_priority
    )
    const typeObj = findLabel(
        NEGOTIATION_TYPES(t),
        negotiation.contract_negotiation_type
    )
    const stageObj = findLabel(
        NEGOTIATION_STAGES(t),
        negotiation.contract_negotiation_stage
    )

    return (
        <div className="mb-4">
            <SubHeaders
                breadcrumbs={[
                    {
                        title: t('negotiations.list'),
                        path: paths.contractNegotiation,
                    },
                    { title: negotiation.contract_negotiation_code, path: '' },
                ]}
            />

            {/* Header card */}
            <Paper className="p-4 mt-2">
                {/* Title + status chips */}
                <div className="flex items-center gap-3 mb-4">
                    <span className="text-base font-semibold text-primary">
                        {negotiation.contract_negotiation_title}
                    </span>
                    {statusObj && (
                        <Chip
                            label={statusObj.label}
                            color={statusObj.color as Color}
                            size="small"
                        />
                    )}
                    {priorityObj && (
                        <Chip
                            label={priorityObj.label}
                            color={priorityObj.color as Color}
                            size="small"
                        />
                    )}
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <InfoItem
                        icon={<Hash className="w-3.5 h-3.5" />}
                        label={t('negotiations.code')}
                        value={negotiation.contract_negotiation_code}
                    />
                    <InfoItem
                        icon={<Layers className="w-3.5 h-3.5" />}
                        label={t('negotiations.type')}
                        value={
                            typeObj?.label ||
                            negotiation.contract_negotiation_type
                        }
                    />
                    <InfoItem
                        icon={<Layers className="w-3.5 h-3.5" />}
                        label={t('negotiations.stage')}
                        value={
                            stageObj ? (
                                <Chip
                                    label={stageObj.label}
                                    color={stageObj.color as Color}
                                    size="small"
                                />
                            ) : (
                                negotiation.contract_negotiation_stage
                            )
                        }
                    />
                    {Number(negotiation.contract_negotiation_value) > 0 && (
                        <InfoItem
                            icon={<DollarSign className="w-3.5 h-3.5" />}
                            label={t('negotiations.value')}
                            value={formatCurrency(
                                Number(negotiation.contract_negotiation_value)
                            )}
                        />
                    )}
                    {negotiation.contract_negotiation_deadline && (
                        <InfoItem
                            icon={<CalendarDays className="w-3.5 h-3.5" />}
                            label={t('negotiations.deadline')}
                            value={formatDateWithFormatter(
                                negotiation.contract_negotiation_deadline
                            )}
                        />
                    )}
                    <InfoItem
                        icon={<User className="w-3.5 h-3.5" />}
                        label={t('negotiations.creator')}
                        value={negotiation.creator_name}
                    />
                    <InfoItem
                        icon={<CalendarDays className="w-3.5 h-3.5" />}
                        label={t('negotiations.created_at')}
                        value={formatDateWithFormatter(negotiation.created_at)}
                    />
                    {/* Progress */}
                    <InfoItem
                        label={t('negotiations.progress')}
                        value={
                            <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                    <div
                                        className="bg-primary h-1.5 rounded-full transition-all"
                                        style={{
                                            width: `${negotiation.contract_negotiation_progress || 0}%`,
                                        }}
                                    />
                                </div>
                                <span className="text-xs text-primary font-medium">
                                    {negotiation.contract_negotiation_progress ||
                                        0}
                                    %
                                </span>
                            </div>
                        }
                    />
                </div>

                {/* Assignees */}
                {negotiation.assignee_names?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5">
                            <User className="w-3.5 h-3.5" />
                            {t('negotiations.assignees')}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {negotiation.assignee_names.map(
                                (name: string, i: number) => (
                                    <span
                                        key={i}
                                        className="bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full"
                                    >
                                        {name}
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                )}

                {/* Action buttons */}
                {(canResolve || canReject || canCancel || canArchive) && (
                    <div className="flex items-center gap-1 flex-wrap justify-end pt-3 mt-3 border-t border-gray-100">
                        {canResolve && (
                            <Button
                                variant="contained"
                                size="small"
                                loading={loadingActions[`resolve_${id}`]}
                                onClick={handleResolve}
                                className="bg-green-600 hover:bg-green-700 text-white"
                            >
                                {t('negotiations.resolve')}
                            </Button>
                        )}
                        {canReject && (
                            <Button
                                variant="contained"
                                size="small"
                                loading={loadingActions[`reject_${id}`]}
                                onClick={handleReject}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {t('reject')}
                            </Button>
                        )}
                        {canCancel && (
                            <Button
                                variant="outlined"
                                size="small"
                                loading={loadingActions[`cancel_${id}`]}
                                onClick={handleCancel}
                            >
                                {t('cancel')}
                            </Button>
                        )}
                        {canArchive && (
                            <Button
                                variant="outlined"
                                size="small"
                                loading={loadingActions[`archive_${id}`]}
                                onClick={handleArchive}
                            >
                                {t('archive')}
                            </Button>
                        )}
                    </div>
                )}
            </Paper>

            {/* Content & Notes */}
            {(negotiation.contract_negotiation_content ||
                negotiation.contract_negotiation_notes) && (
                <Paper className="mt-2 space-y-3">
                    {negotiation.contract_negotiation_content && (
                        <div>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                <MessageSquare className="w-3.5 h-3.5" />
                                {t('negotiations.content')}
                            </div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {negotiation.contract_negotiation_content}
                            </div>
                        </div>
                    )}
                    {negotiation.contract_negotiation_notes && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="flex items-center gap-1.5 text-xs text-amber-700 mb-1">
                                <StickyNote className="w-3.5 h-3.5" />
                                {t('negotiations.notes')}
                            </div>
                            <div className="text-sm text-amber-800 whitespace-pre-wrap">
                                {negotiation.contract_negotiation_notes}
                            </div>
                        </div>
                    )}
                </Paper>
            )}

            {/* Attachments */}
            {negotiation.attachments?.length > 0 && (
                <Paper className="mt-2">
                    <ContractAttachment
                        attachments={
                            uniqBy(negotiation.attachments, 'attachment_id') ||
                            []
                        }
                    />
                </Paper>
            )}

            {/* History */}
            <Paper className="mt-2">
                <History negotiationID={negotiation.contract_negotiation_id} />
            </Paper>
        </div>
    )
}

export default Detail
